import React, { FC, MouseEventHandler, ReactNode } from 'react';
import { OriginalsSelectionEntity } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { RouterLocation } from '@funtech-int/rival';
import classnames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { createPictureFactory, Picture } from '@funtech-int/yango-shared';

import { RouterLink } from '@/shared/components/RouterLink/RouterLink';

import * as CardKit from '../CardKit';
import { useAnnounceI18n } from '../../hooks/useAnnouncePromiseI18n';

import styles from './_Original.module.css';

const horizontalPosterPictureFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '375x234',
            '2x': '640x360', // actually 1.7x but this is maximum available size
        },
        src: '375x234',
    },
});

const verticalPosterPictureFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '375x562',
            '2x': '502x753',
        },
        src: '375x562',
    },
});

interface OriginalProps {
    entity: OriginalsSelectionEntity;
    location: RouterLocation<any, any>;
    isMinimized?: boolean;
    onShow?: VoidFunction;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
}

export const Original: FC<OriginalProps> = ({ entity, location, isMinimized, onShow, onClick }) => {
    const { movie } = entity;

    const { ref } = useInView({
        onChange(inView: boolean) {
            if (inView) {
                onShow?.();
            }
        },
        threshold: 0.8,
    });

    const announceI18n = useAnnounceI18n();

    let announceSlot: ReactNode;

    if (movie.availabilityAnounce) {
        const { availabilityAnounce } = movie;

        announceSlot = (
            <CardKit.Announce
                text={announceI18n.getPromise(availabilityAnounce.announcePromise)}
                availabilityDate={availabilityAnounce.availabilityDate}
                footerText={
                    availabilityAnounce.isSeasonAnnounce ? announceI18n.getSeason() : undefined
                }
            />
        );
    }

    const { img: horizontalPosterImg } = horizontalPosterPictureFactory({
        fallbackUrl: movie.horizontalPoster?.toString(),
    });

    const { img: verticalPosterImg } = verticalPosterPictureFactory({
        fallbackUrl: entity.startCover?.toString(),
    });

    const { img: verticalPosterFinishImg } = verticalPosterPictureFactory({
        fallbackUrl: entity.finishCover?.toString(),
    });

    const { img: verticalPosterOverlayImg } = verticalPosterPictureFactory({
        fallbackUrl: entity.backgroundCover?.toString(),
    });

    return (
        <RouterLink
            to={location}
            className={classnames(
                styles.root,
                isMinimized ? styles.isMinimized : styles.isMaximized
            )}
            onClick={onClick}
            ref={ref}
            dataTid="CardOriginal"
        >
            <div className={styles.shadowBorder} />
            <Picture
                className={styles.horizontalPoster}
                alt={movie.title.toString()}
                loading="lazy"
                srcSet={horizontalPosterImg.srcSet}
                src={horizontalPosterImg.src}
                data-tid="AdaptiveImage"
            />
            {entity.startCover ? (
                <Picture
                    className={styles.verticalPoster}
                    alt=""
                    loading="lazy"
                    srcSet={verticalPosterImg.srcSet}
                    src={verticalPosterImg.src}
                    data-tid="CardOriginalPoster"
                />
            ) : null}
            {entity.finishCover ? (
                <Picture
                    className={styles.verticalPosterHover}
                    alt=""
                    loading="lazy"
                    srcSet={verticalPosterFinishImg.srcSet}
                    src={verticalPosterFinishImg.src}
                    data-tid="CardOriginalPosterHover"
                />
            ) : null}
            {entity.backgroundCover ? (
                <Picture
                    className={styles.verticalPosterOverlay}
                    alt=""
                    loading="lazy"
                    srcSet={verticalPosterOverlayImg.srcSet}
                    src={verticalPosterOverlayImg.src}
                    data-tid="CardOriginalPosterOverlay"
                />
            ) : null}
            {announceSlot}
        </RouterLink>
    );
};
